import { ActionTree } from 'vuex';
import { PreludeAction as PreludeActionType } from '@sandbox/api-sdk';
import { IRootState } from '@/store/types';
import { IState, PreludePayload, ContentChangePayload } from './types';
import { getFcToken } from '@/utils/common';
import {
  getFolder,
  getFile,
  updateCacheFile,
  prelude,
  File,
  PoolName,
  setCacheFile
} from '@/sandbox';
import { getStorage } from '@/server/model/timeline';
import router from '@/router';
import { isProd } from '@/sandbox-utils/utils';

const actions: ActionTree<IState, IRootState> = {
  async getStorage({ commit, state }, force = false) {
    if (!state.type || state.loading) return;
    commit('setLoading', true);
    await getFcToken();
    const res = await getStorage().catch((err) => {
      commit('setLoading', false);
      throw err;
    });
    commit('setLoading', false);
    if (res.data) {
      commit('setSandboxConfig', {
        poolName: PoolName[state.type],
        isProd,
        ...res.data
      });
      commit('setLimitState', null);
    } else {
      commit('setLimitState', res);
    }
  },
  contentChange({ commit }, data: ContentChangePayload) {
    commit('setContentChange', data);
  },
  async prelude({ state, commit }, data: PreludePayload) {
    const { rootPath, compress, type } = state;
    if (!type || !compress?.url) return;
    return prelude(
      {
        source: compress.url,
        rootPath,
        path: data.path,
        action: data.action
      },
      type
    );
  },
  async reset({ state, commit, dispatch }, data: File) {
    const { compress, files, rootPath, type } = state;
    if (!compress || !type) return;
    commit('setResetPath', data.path);
    try {
      await prelude(
        {
          source: compress.url,
          rootPath,
          path: data.path,
          action: PreludeActionType.reset
        },
        type
      );
      const updateFiles = await updateCacheFile(data, type, compress.files);
      const updatePaths = updateFiles?.map((item) => item.path);
      if (updateFiles?.length) {
        let isExit = false;
        const res = files.map((item: File) => {
          const idx = updatePaths?.findIndex((path) => path === item.path);
          if (idx !== undefined && idx > -1) {
            isExit = true;
            return { ...item, ...updateFiles[idx], isSelected: item.isSelected, isUpdated: true };
          }
          return item;
        });
        if (isExit) {
          commit('setFiles', { files: res });
        }
      }
      commit('setResetPath', '');
    } catch (error) {
      commit('setResetPath', '');
    }
  },
  async getTrees({ state, commit }, path = '') {
    if (!state.type) return;
    const trees = await getFolder(
      { path: path || state.rootPath, isDir: true } as any,
      state.type,
      state.compress?.files
    );
    commit('setTrees', trees);
  },
  async selectFile({ state, commit }, file: File) {
    if (!state.type) return;
    const { files: currentFiles, compress, type } = state;
    let has = false;
    const files = currentFiles.map((item: File) => {
      if (item.isSelected) {
        return { ...item, isSelected: false, isUpdated: false };
      }
      if (file.path === item.path) {
        has = true;
        return { ...item, isSelected: true, isUpdated: false };
      }
      return item;
    });
    let func = null;
    if (!has) {
      const preludeFiles = compress?.files;
      const re = (await getFile(file, type, true, preludeFiles)) as any;
      if (re) {
        func = re[0];
        files.push({ ...re[1], isSelected: true });
      }
    }
    commit('setFiles', { files });
    func?.().then((file: File) => {
      commit('setFile', { file });
    });
  },
  async updateFile(
    { state, commit },
    { file, isForce, isSelected }: { file: File; isForce: boolean; isSelected?: boolean }
  ) {
    if (!state.type) return;
    const [fetchFunc, pendingFile] = await (getFile(
      file,
      state.type,
      true,
      state.compress?.files,
      isForce || false
    ) as any);
    commit('setFile', { file: { ...pendingFile, isSelected: isSelected ? isSelected : false } });

    fetchFunc?.().then((file: File) => {
      commit('setFile', { file });
    });
  },
  async closeFile({ commit, state }, file) {
    let files = state.files.filter((item: File) => item.path !== file.path);
    if (file.isSelected) {
      files = files.map((item: File, index: number) => {
        if (index === files.length - 1) return { ...item, isSelected: true, isUpdated: false };
        return item;
      });
    }
    commit('setFiles', { files });
  },
  setIsUpdated({ commit, state }, { value, file }) {
    if (file) {
      commit('setFile', { file: { ...file, isUpdated: value } });
      return;
    }
    const files = state.files.map((item) => {
      return { ...item, isUpdated: value };
    });
    commit('setFiles', { files });
  }
};

export default actions;
